import { inject, Injectable } from '@angular/core';
import { Theme, ThemeService } from '@hs/ui-core-presentation';

import { PDFExportService } from './pdf-export.service';

@Injectable()
export class DashboardPDFExportService {
    private readonly pdfExportService = inject(PDFExportService);
    private readonly themeService = inject(ThemeService);

    private theme: Theme = Theme.light;

    constructor() {
        this.themeService.currentTheme$
            .subscribe({
                next: (theme: Theme) => this.theme = theme,
            });
    }

    async exportDashboardToPDF(fileName: string): Promise<void> {
        const dashboard = this.getAndModifyDashboard();

        if (dashboard) {
            await this.pdfExportService.generatePDF(
                dashboard.cloned,
                fileName,
                dashboard.original.clientWidth,
                dashboard.original.clientHeight,
                this.theme,
            );
        }
    }

    private getAndModifyDashboard(): { original: Element, cloned: HTMLElement } | void {
        const dashboard = document.querySelector('#layout-wrapper') as HTMLElement;
        if (!dashboard) {
            return console.error('Cannot export dashboard to PDF without a dashboard');
        }

        const clonedDashboard = this.cloneDashboard(dashboard);
        this.removeDashboardElements(clonedDashboard, ['app-dashboard-export']);
        this.removeAllScrollbars(clonedDashboard);
        if (document.querySelector('ag-grid-angular')) {
            this.updateGridIcons(clonedDashboard);
        }

        return { original: dashboard, cloned: clonedDashboard };
    }

    private removeAllScrollbars(dashboard: HTMLElement): void {
        const filtersBar = dashboard.querySelector('.subscribed-filter-wrapper');
        if (filtersBar) {
            (filtersBar as HTMLElement).style.overflow = 'hidden';
        }

        const summaryWidgets = dashboard.querySelectorAll('.summary-wrap .summary-content');
        if (summaryWidgets.length) {
            summaryWidgets.forEach((w) => (w as HTMLElement).style.overflow = 'hidden');
        }

        const gridWidgets = dashboard.querySelectorAll('.ag-body-horizontal-scroll, .ag-body-vertical-scroll');
        if (gridWidgets.length) {
            gridWidgets.forEach((w) => (w as HTMLElement).style.display = 'none');
        }
    }

    private cloneDashboard(dashboard: HTMLElement): HTMLElement {
        const clonedDashboard = dashboard.cloneNode(true) as HTMLElement;

        this.attachClonedDashboardToDummyContainer(clonedDashboard, dashboard);

        return clonedDashboard;
    }

    private attachClonedDashboardToDummyContainer(clonedDashboard: HTMLElement, dashboard: HTMLElement): void {
        const container = document.createElement('div');
        container.id = 'cloned-dashboard';
        container.style.position = 'fixed';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = `${dashboard.offsetWidth}px`;
        container.style.height = `${dashboard.offsetHeight}px`;
        container.style.zIndex = '-10000';
        container.style.opacity = '0';
        container.appendChild(clonedDashboard);
        document.body.appendChild(container);
    }

    private removeDashboardElements(dashboard: HTMLElement, elementsTag: string[]): void {
        elementsTag.forEach((elementTag) => {
            const elements = dashboard.getElementsByTagName(elementTag);
            if (elements.length) {
                const elementParent = elements[0].parentNode;
                elementParent?.removeChild(elements[0]);
            }
        });
    }

    private updateGridIcons(dashboard: Element): void {
        this.updateGridIconImage(dashboard, 'ag-icon-none', `./assets/images/sorting-${this.theme}.png`);
        this.updateGridIconImage(dashboard, 'sort-ascending', `./assets/images/sort-ascending-${this.theme}.png`);
        this.updateGridIconImage(dashboard, 'sort-descending', `./assets/images/sort-descending-${this.theme}.png`);
        this.updateGridIconImage(dashboard, 'custom-checkbox-checked', `./assets/images/checkboxChecked-${this.theme}.png`);
        this.updateGridIconImage(dashboard, 'custom-checkbox-unchecked', `./assets/images/checkboxUnchecked-${this.theme}.png`);
        this.updateGridIconImage(dashboard, 'ag-column-drop-cell-button', './assets/images/close.png');
    }

    private updateGridIconImage(dashboard: Element, iconSelector: string, imagePath: string): void {
        const icons = dashboard.getElementsByClassName(iconSelector);
        for (let i = 0, j = icons.length; i < j; i++) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((icons[i] as HTMLElement).style as any)['background-image'] = `url(${imagePath})`;
        }
    }
}

